/**
 * Top-rated collections — tabs + product slider.
 * Tag: <zoh-top-rated-collections>
 *
 * Keyboard (APG-aligned):
 * - Focus on a tab: ArrowLeft/ArrowRight cycle tabs; Home/End jump to first/last.
 * - Focus on the product track: ArrowLeft/ArrowRight scroll the active carousel
 *   (when it can scroll), including on mobile. Up/Down are not used for scrolling
 *   so they do not conflict with page scroll or tab navigation.
 */
class ZohTopRatedCollections extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._tabs = Array.from(this.querySelectorAll('[data-zoh-trc-tab]'));
    this._panels = Array.from(this.querySelectorAll('[data-zoh-trc-panel]'));
    this._tabsContainer = this.querySelector('[data-zoh-trc-tabs]');
    this._nav = this.querySelector('[data-zoh-trc-nav]');
    this._prev = this.querySelector('[data-zoh-trc-prev]');
    this._next = this.querySelector('[data-zoh-trc-next]');
    this._viewAll = this.querySelector('[data-zoh-trc-view-all]');
    this._activeTrack = null;
    this._activeIndex = 0;
    this._rafId = null;
    this._gap = 21;

    const gapValue = this.getAttribute('data-gap');
    if (gapValue) {
      const parsed = Number.parseFloat(gapValue);
      if (!Number.isNaN(parsed)) this._gap = parsed;
    }

    const hasUsableServerTabs =
      this._tabs.length > 0 &&
      this._tabs.every((tab) => (tab.textContent?.trim() || '').length > 0);

    if (!hasUsableServerTabs && this._panels.length > 0) {
      this._buildTabsFromPanels();
      this._tabs = Array.from(this.querySelectorAll('[data-zoh-trc-tab]'));
    }

    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this._prev?.addEventListener('click', this._onPrev);
    this._next?.addEventListener('click', this._onNext);
    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKeydown);
    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('load', this._onResize, { passive: true });

    if (typeof this._mobileQuery.addEventListener === 'function') {
      this._mobileQuery.addEventListener('change', this._onResize);
    } else {
      this._mobileQuery.addListener(this._onResize);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._scheduleUpdate());
      this._resizeObserver.observe(this);
      this._panels.forEach((panel) => this._resizeObserver.observe(panel));
    }

    this._activate(this._getInitialIndex(), false);
    requestAnimationFrame(() => this._scheduleUpdate());
  }

  _buildTabsFromPanels() {
    if (!(this._tabsContainer instanceof HTMLElement)) return;

    this._tabsContainer.innerHTML = '';

    this._panels.forEach((panel, index) => {
      const panelId = panel.id || `zoh-trc-panel-${index + 1}`;
      const viewport = panel.querySelector('.zoh-top-rated-collections__viewport');
      const href = panel.getAttribute('data-zoh-trc-collection-url') || '';
      const handleLabel = href.split('/').filter(Boolean).pop()?.replace(/[-_]+/g, ' ').trim() || '';
      const label =
        panel.getAttribute('data-zoh-trc-tab-label') ||
        viewport?.getAttribute('aria-label') ||
        handleLabel.toUpperCase() ||
        `Collection ${index + 1}`;
      const labelledBy = panel.getAttribute('aria-labelledby') || `zoh-trc-tab-generated-${index + 1}`;

      panel.setAttribute('aria-labelledby', labelledBy);

      const button = document.createElement('button');
      button.type = 'button';
      button.id = labelledBy;
      button.className = `zoh-top-rated-collections__tab${index === 0 ? ' is-active' : ''}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.setAttribute('data-zoh-trc-tab', '');
      button.setAttribute('data-panel-id', panelId);
      button.textContent = label;

      this._tabsContainer.appendChild(button);
    });

    this._tabsContainer.hidden = false;
  }

  disconnectedCallback() {
    this._prev?.removeEventListener('click', this._onPrev);
    this._next?.removeEventListener('click', this._onNext);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onResize);

    if (this._mobileQuery) {
      if (typeof this._mobileQuery.removeEventListener === 'function') {
        this._mobileQuery.removeEventListener('change', this._onResize);
      } else {
        this._mobileQuery.removeListener(this._onResize);
      }
    }

    if (this._activeTrack) {
      this._activeTrack.removeEventListener('scroll', this._onScroll);
    }

    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _getInitialIndex() {
    const activeIndex = this._tabs.findIndex((tab) => tab.classList.contains('is-active'));
    return activeIndex >= 0 ? activeIndex : 0;
  }

  _isMobile() {
    return this._mobileQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  _getActiveTrack() {
    const activePanel = this._panels[this._activeIndex];
    return activePanel ? activePanel.querySelector('[data-zoh-trc-track]') : null;
  }

  _canScroll() {
    if (!this._activeTrack) return false;
    const slideCount = this._activeTrack.querySelectorAll('.zoh-top-rated-collections__slide').length;
    return slideCount > 4 || this._activeTrack.scrollWidth > this._activeTrack.clientWidth + 2;
  }

  _shouldShowNav() {
    return !this._isMobile() && this._canScroll();
  }

  _activate(index, shouldFocus) {
    if (!this._tabs[index] || !this._panels[index]) return;

    this._activeIndex = index;

    this._tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && shouldFocus) tab.focus();
    });

    this._panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    });

    if (this._activeTrack) {
      this._activeTrack.removeEventListener('scroll', this._onScroll);
    }

    this._activeTrack = this._getActiveTrack();

    if (this._activeTrack) {
      this._activeTrack.scrollLeft = 0;
      this._activeTrack.addEventListener('scroll', this._onScroll, { passive: true });
    }

    this._syncViewAll();
    this._scheduleUpdate();
  }

  _syncViewAll() {
    if (!(this._viewAll instanceof HTMLAnchorElement)) return;

    const activePanel = this._panels[this._activeIndex];
    const href = activePanel ? activePanel.getAttribute('data-zoh-trc-collection-url') || '' : '';

    if (href) {
      this._viewAll.href = href;
      this._viewAll.hidden = false;
    } else {
      this._viewAll.hidden = true;
      this._viewAll.removeAttribute('href');
    }
  }

  _syncSliderActive() {
    const showNav = this._shouldShowNav();
    this.toggleAttribute('data-slider-active', showNav);

    if (!this._nav) return;

    if (showNav) {
      this._nav.removeAttribute('hidden');
      this._nav.setAttribute('aria-hidden', 'false');
    } else {
      this._nav.setAttribute('hidden', '');
      this._nav.setAttribute('aria-hidden', 'true');
    }
  }

  _getStep() {
    const card = this._activeTrack?.querySelector('.zoh-top-rated-collections__slide');
    const width = card instanceof HTMLElement ? card.offsetWidth : 206;
    const styles = this._activeTrack ? getComputedStyle(this._activeTrack) : null;
    const gapValue = styles?.gap || styles?.columnGap || '';
    const gap = Number.parseFloat(gapValue);
    const resolvedGap = Number.isNaN(gap) ? this._gap : gap;
    return width + resolvedGap;
  }

  _scrollByDir(direction) {
    if (!this._activeTrack || !this._canScroll()) return;
    this._activeTrack.scrollBy({ left: direction * this._getStep(), behavior: 'smooth' });
  }

  /**
   * Mark slides fully outside the active track viewport as inert.
   */
  _updateSlideInert() {
    if (!this._activeTrack) return;

    const trackRect = this._activeTrack.getBoundingClientRect();
    const slides = this._activeTrack.querySelectorAll('.zoh-top-rated-collections__slide');

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const slideRect = slide.getBoundingClientRect();
      const visible =
        slideRect.right > trackRect.left + 1 && slideRect.left < trackRect.right - 1;

      if (visible) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }

  _updateControls() {
    if (!this._activeTrack) return;

    const showNav = this._shouldShowNav();
    const maxScroll = Math.max(0, this._activeTrack.scrollWidth - this._activeTrack.clientWidth);
    const left = this._activeTrack.scrollLeft;
    const epsilon = 2;

    if (this._prev) {
      this._prev.disabled = !showNav || left <= epsilon;
    }

    if (this._next) {
      this._next.disabled = !showNav || left >= maxScroll - epsilon;
    }
  }

  _scheduleUpdate() {
    if (this._rafId !== null) cancelAnimationFrame(this._rafId);
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._syncSliderActive();
      this._updateControls();
      this._updateSlideInert();
    });
  }

  _onPrev() {
    this._scrollByDir(-1);
  }

  _onNext() {
    this._scrollByDir(1);
  }

  _onResize() {
    this._scheduleUpdate();
  }

  _onScroll() {
    this._updateControls();
    this._updateSlideInert();
  }

  _onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-zoh-trc-tab]');
    if (tab && this.contains(tab)) {
      const index = this._tabs.indexOf(tab);
      if (index >= 0) {
        event.preventDefault();
        this._activate(index, true);
      }
    }
  }

  _onKeydown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tabEl = target.closest('[data-zoh-trc-tab]');
    const currentTabIndex = tabEl && this.contains(tabEl) ? this._tabs.indexOf(tabEl) : -1;

    // APG tabs: Left/Right cycle; Home/End jump to ends.
    if (currentTabIndex >= 0) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._activate((currentTabIndex + 1) % this._tabs.length, true);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._activate((currentTabIndex - 1 + this._tabs.length) % this._tabs.length, true);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this._activate(0, true);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        this._activate(this._tabs.length - 1, true);
        return;
      }

      return;
    }

    // Product track: Left/Right scroll when the carousel can scroll (incl. mobile).
    const onTrack = Boolean(target.closest('[data-zoh-trc-track]'));
    if (!onTrack || !this._canScroll()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this._scrollByDir(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this._scrollByDir(1);
    }
  }
}

if (!customElements.get('zoh-top-rated-collections')) {
  customElements.define('zoh-top-rated-collections', ZohTopRatedCollections);
}
